import { usePercent } from '@afterpaytouch/core'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { useTranslation } from '../../utils/convergence'
import { PaymentInterval } from '../../model'
import {
  useAppRouter,
  usePaymentInterval,
  useOfferDetailPCL,
  useOfferDetailPBI,
  useHasZeroAprConsumerLendingPromotion,
  useMerchantName,
  useIsAfterpayFinanceFeeEligible,
  useSUPFinanceFee,
  useInstallmentAmount,
  useAmountDueToday,
} from '../../state'
import { Currency } from '@afterpay/types'
import { useMoney } from '../Money/useMoney'

const useFormatMoney = (currency?: Currency, decimalPlaces: number = 2): ((amount?: string) => string) => {
  const { locale } = useAppRouter()
  const { formatMoney } = useMoney({
    locale,
    currency,
    decimalPlaces,
  })
  return formatMoney
}

export const getConsumerInitials = (name: string): string => {
  const getFirstLetterOfWord = (word: string | undefined): string => {
    return word !== undefined ? word[0] : ''
  }

  const words: string[] = name.split(' ')
  const firstWord = words.shift()
  const lastWord = words.pop()
  return [getFirstLetterOfWord(firstWord), getFirstLetterOfWord(lastWord)].join('').toUpperCase()
}

export const usePCLFromText = (): string => {
  const { t } = useTranslation()
  const { monthlyAmount } = useOfferDetailPCL() ?? {}

  const formatMoneyForMonthlyAmount = useFormatMoney(monthlyAmount?.currency)

  return t('consumerLending:paymentOptions:payInMoreAmount', {
    monthlyAmount: formatMoneyForMonthlyAmount(monthlyAmount?.amount),
  })
}

export const usePCLNumberOfInstalmentsText = (): string => {
  const { t } = useTranslation()
  const { numberOfInstallments, amountDueToday } = useOfferDetailPCL() ?? {}

  const formatMoney = useFormatMoney(amountDueToday?.currency, 0)

  return t('consumerLending:paymentOptions:upToNumberOfInstallmentsMonths', {
    numberOfInstallments,
    amountDueToday: formatMoney(amountDueToday?.amount),
  })
}

export const usePCLAsLowAsText = (): string => {
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const { apr } = useOfferDetailPCL() ?? {}
  const { t } = useTranslation()
  const hasZeroAprPromotion = useHasZeroAprConsumerLendingPromotion()
  const _ = useMerchantName()

  return hasZeroAprPromotion
    ? t('consumerLending:paymentOptions:payInMoreWithZeroAPR')
    : t('consumerLending:paymentOptions:payInMoreAPR', { lowestPercentage: formatPercent(apr) })
}

export const usePBIDueTodayText = (): string => {
  const { t } = useTranslation()
  const amountDueToday = useAmountDueToday()
  const offerAmountDueToday = useOfferDetailPBI()?.amountDueToday
  const updatedAmountDueToday = amountDueToday ?? offerAmountDueToday

  const formatMoney = useFormatMoney(updatedAmountDueToday?.currency)

  return t('consumerLending:paymentOptions:dueToday', {
    amountDueToday: formatMoney(updatedAmountDueToday?.amount),
  })
}

export const usePBIEveryText = (): string => {
  const { t } = useTranslation()
  const paymentInterval = usePaymentInterval()
  const installmentAmount = useInstallmentAmount()
  const offerInstallmentAmount = useOfferDetailPBI()?.monthlyAmount
  const monthlyAmount = installmentAmount ?? offerInstallmentAmount
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const formatMoney = useFormatMoney(monthlyAmount?.currency)

  const translationParams = { instalmentAmount: formatMoney(monthlyAmount?.amount), context: isAFF ? 'aff' : undefined }
  return paymentInterval === PaymentInterval.FORTNIGHTLY
    ? t('consumerLending:paymentOptions:fortnightlyPaymentInterval', translationParams)
    : t('consumerLending:paymentOptions:monthlyPaymentInterval', translationParams)
}

export const usePBIAPRText = (): string => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const supFinanceFee = useSUPFinanceFee()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 0, shouldDivideBy100: true })
  const interestAmount = '0.00'
  const apr = formatPercent(0)

  const { monthlyAmount } = useOfferDetailPBI() ?? {}

  const formatMoney = useFormatMoney(monthlyAmount?.currency)

  if (isAFF) {
    return t('consumerLending:paymentOptions:payInFourAFF', {
      financeFee: formatMoney(supFinanceFee?.variableAmount.amount),
    })
  }

  return t('consumerLending:paymentOptions:payInFourAPR', {
    apr,
    interestAmount: formatMoney(interestAmount),
  })
}

export const usePBIRejectedText = (): string => {
  const { t } = useTranslation()
  const offerDetailPBI = useOfferDetailPBI()

  switch (offerDetailPBI?.rejectedReasonCode) {
    case ErrorCode.MaxActiveOrdersReached:
      return t('consumerLending:paymentTypesPBIDeclined:maxActiveOrdersReached', {
        interval: offerDetailPBI?.numberOfInstallments,
      })

    case ErrorCode.AmountTooHigh:
    case ErrorCode.OrderAmountTooHigh:
      return t('consumerLending:paymentTypesPBIDeclined:amountTooHigh', {
        interval: offerDetailPBI?.numberOfInstallments,
      })

    case ErrorCode.MerchantOrderAmountLimitExceeded:
      return t('consumerLending:paymentTypesPBIDeclined:merchantOrderAmountLimitExceeded', {
        interval: offerDetailPBI?.numberOfInstallments,
      })

    default:
      return t('consumerLending:paymentTypesPBIDeclined:genericDecline', {
        interval: offerDetailPBI?.numberOfInstallments,
      })
  }
}
